/**
 * Date Utility Functions for RSH Portal
 * Handles timezone conversion for Airtable dates
 */

/**
 * Formats a date string from Airtable to display in Eastern Time (ET)
 * 
 * @param {string|Date} dateString - Date from Airtable (e.g., "2025-01-15")
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeTime - Whether to include time in output
 * @param {string} options.timezone - Target timezone (default: 'America/New_York')
 * @returns {string} Formatted date string
 * 
 * @example
 * formatDateET("2025-01-15") // "Jan 15, 2025"
 * formatDateET("2025-01-15T14:30:00.000Z", { includeTime: true }) // "Jan 15, 2025, 9:30 AM"
 */
export const formatDateET = (dateString, options = {}) => {
  if (!dateString) return "TBD";

  const {
    includeTime = false,
    timezone = "America/New_York",
  } = options;

  try {
    // Create date object
    // If the string doesn't have a time component, add noon to avoid timezone issues
    const dateToFormat = dateString.includes("T")
      ? new Date(dateString)
      : new Date(`${dateString}T12:00:00`);

    // Format options
    const formatOptions = {
      timeZone: timezone,
      year: "numeric",
      month: "short",
      day: "numeric",
    };

    if (includeTime) {
      formatOptions.hour = "numeric";
      formatOptions.minute = "2-digit";
      formatOptions.hour12 = true;
    }

    return new Intl.DateTimeFormat("en-US", formatOptions).format(dateToFormat);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Invalid Date";
  }
};

/**
 * Formats a date to display in the user's local timezone
 * 
 * @param {string|Date} dateString - Date from Airtable
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string in local time
 * 
 * @example
 * formatDateLocal("2025-01-15") // "Jan 15, 2025" (in user's local timezone)
 */
export const formatDateLocal = (dateString, options = {}) => {
  if (!dateString) return "TBD";

  const { includeTime = false } = options;

  try {
    const dateToFormat = dateString.includes("T")
      ? new Date(dateString)
      : new Date(`${dateString}T12:00:00`);

    const formatOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
    };

    if (includeTime) {
      formatOptions.hour = "numeric";
      formatOptions.minute = "2-digit";
      formatOptions.hour12 = true;
    }

    return new Intl.DateTimeFormat("en-US", formatOptions).format(dateToFormat);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Invalid Date";
  }
};

/**
 * Calculates days between a date and today in Eastern Time
 * Used for selection due date calculations
 * 
 * @param {string|Date} dateString - Target date
 * @returns {number} Days until date (negative if past)
 * 
 * @example
 * getDaysUntilET("2025-01-15") // Returns number of days
 */
export const getDaysUntilET = (dateString) => {
  if (!dateString) return null;

  try {
    // Get current date in ET
    const nowET = new Date(
      new Date().toLocaleString("en-US", { timeZone: "America/New_York" })
    );
    nowET.setHours(0, 0, 0, 0);

    // Parse the target date
    const targetDate = dateString.includes("T")
      ? new Date(dateString)
      : new Date(`${dateString}T12:00:00`);
    
    // Convert target to ET
    const targetET = new Date(
      targetDate.toLocaleString("en-US", { timeZone: "America/New_York" })
    );
    targetET.setHours(0, 0, 0, 0);

    // Calculate difference in days
    const diffTime = targetET - nowET;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  } catch (error) {
    console.error("Error calculating days until:", error);
    return null;
  }
};

/**
 * Checks if a date is in the past (in Eastern Time)
 * 
 * @param {string|Date} dateString - Date to check
 * @returns {boolean} True if date is in the past
 */
export const isPastDateET = (dateString) => {
  const days = getDaysUntilET(dateString);
  return days !== null && days < 0;
};

/**
 * Formats a date for API requests (ISO format)
 * Ensures consistent formatting when sending dates to Firebase/Airtable
 * 
 * @param {Date} date - JavaScript Date object
 * @returns {string} ISO date string (YYYY-MM-DD)
 */
export const formatDateForAPI = (date) => {
  if (!date) return "";
  
  try {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error("Error formatting date for API:", error);
    return "";
  }
};

/**
 * Parses an Airtable date string to a Date object
 * Handles dates with and without time components
 * 
 * @param {string} dateString - Date string from Airtable
 * @returns {Date|null} Date object or null if invalid
 */
export const parseAirtableDate = (dateString) => {
  if (!dateString) return null;

  try {
    // If no time component, add noon to avoid timezone issues
    const dateWithTime = dateString.includes("T")
      ? dateString
      : `${dateString}T12:00:00`;
    
    return new Date(dateWithTime);
  } catch (error) {
    console.error("Error parsing Airtable date:", error);
    return null;
  }
};

/**
 * Gets a relative time string (e.g., "3 days ago", "in 5 days")
 *
 * @param {string|Date} dateString - Date to compare
 * @returns {string} Relative time string
 */
export const getRelativeTime = (dateString) => {
  const days = getDaysUntilET(dateString);

  if (days === null) return "Unknown";
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  if (days === -1) return "Yesterday";
  if (days > 0) return `in ${days} days`;
  return `${Math.abs(days)} days ago`;
};

/**
 * Gets a fine-grained "time ago" string (e.g., "Just now", "5 min ago",
 * "3 hours ago", "2 days ago"). Always reports past durations relative to
 * "now" in Eastern Time. Used by the Admin Uploads recent feed where the
 * day-granularity getRelativeTime is too coarse.
 *
 * @param {string|Date} dateString - Past timestamp (ISO string preferred)
 * @returns {string} Human-readable elapsed time
 *
 * @example
 * getTimeAgoET("2026-04-16T14:30:00Z") // "5 min ago"
 */
export const getTimeAgoET = (dateString) => {
  if (!dateString) return "Unknown";

  try {
    const past = new Date(dateString);
    if (Number.isNaN(past.getTime())) return "Unknown";

    const diffMs = Date.now() - past.getTime();
    if (diffMs < 0) return "Just now";

    const seconds = Math.floor(diffMs / 1000);
    if (seconds < 45) return "Just now";

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;

    const weeks = Math.floor(days / 7);
    if (weeks < 5) return `${weeks} week${weeks === 1 ? "" : "s"} ago`;

    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;

    const years = Math.floor(days / 365);
    return `${years} year${years === 1 ? "" : "s"} ago`;
  } catch (error) {
    console.error("Error formatting time ago:", error);
    return "Unknown";
  }
};

export default {
  formatDateET,
  formatDateLocal,
  getDaysUntilET,
  isPastDateET,
  formatDateForAPI,
  parseAirtableDate,
  getRelativeTime,
  getTimeAgoET,
};
